import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  Box,
  Flex,
  Heading,
  Progress,
  SimpleGrid,
  Text,
  Button,
  Center,
  Icon,
  HStack,
  Divider,
  useColorMode,
  IconButton,
  Tooltip,
  Spacer,
  Spinner,
  useBreakpointValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useClipboard,
  useDisclosure,
} from "@chakra-ui/react";
import { ArrowUpIcon } from "@chakra-ui/icons";
import { useParams, useHistory } from "react-router-dom";
import { FaShareAlt, FaSuitcase, FaUserPlus } from "react-icons/fa";
import { PlusSquareIcon } from "@chakra-ui/icons";
import { FaClipboardList } from "react-icons/fa";

import { BsThreeDotsVertical } from "react-icons/bs";
import { ReactComponent as DownloadIcon } from "../../assets/icons/download_2_fill_icon.svg";

import axiosInstance from "../../helpers/axiosInstance";
import { useTranslation } from "../LanguageTranslation/TranslationContext";
import Translate from "../LanguageTranslation/Translate";

import PackingListTrips from "./PackingListTrips";
import TripsList from "./TripsList";
import PackingListModal from "./PackingListModal";
import PackingListCard from "./PackingListCard";
import PackingListDetails from "./PackingListDetails";
import usePersistedState from "../usePersistedState";
import qrCode from "../../assets/images/qr-code.png";
import officialLogo from "../../assets/images/official-logo.png";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import SettingsMenu from "../common/SettingsMenu";
import { Helmet } from "react-helmet-async";

import Loading from "../common/Loading";
import InviteManagerModal from "../common/InviteManagerModal";
import TravelGroupCircles from "../common/TravelGroupCircles";

const PackingListMenu = () => {
  const toast = useToast();
  const { tripId } = useParams();
  const { colorMode } = useColorMode();
  const history = useHistory();
  const { translateText } = useTranslation();
  const isBaseScreen = useBreakpointValue({ base: true, md: false });
  const inviteModal = useDisclosure();
  const [selectedTrip, setSelectedTrip] = usePersistedState(
    "selectedTripPackingList",
    ""
  );
  const [tripName, setTripName] = useState("");
  const [tripMembers, setTripMembers] = useState([]);
  const [packingLists, setPackingLists] = useState([]);
  const [selectedListId, setSelectedIdList] = useState(null);
  const [trips, setTrips] = useState([]);

  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Share modal state
  const [isShareOpen, setIsShareOpen] = useState(false);
  const onShareOpen = () => setIsShareOpen(true);
  const onShareClose = () => setIsShareOpen(false);
  const shareUrl = `${window.location.origin}/ai-travel-planner/packing-list/${selectedTrip}`;
  const { hasCopied, onCopy } = useClipboard(shareUrl);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const fetchPackingLists = useCallback(async () => {
    if (selectedTrip === "selectedTripPackingList" || selectedTrip === "") {
      return;
    }

    try {
      const response = await axiosInstance.get(
        `/packing_lists/${selectedTrip}/`
      );
      if (response.data && Array.isArray(response.data.data)) {
        setPackingLists(response.data.data);
      } else {
        toast({
          title: "Error",
          description: "Failed to fetch packing lists: Invalid data format.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      // toast({
      //   title: "Error",
      //   description: "Failed to fetch packing lists.",
      //   status: "error",
      //   duration: 3000,
      //   isClosable: true,
      // });
    }
  }, [selectedTrip, toast]);

  const fetchTripMembers = async () => {
    if (selectedTrip === "selectedTripPackingList" || selectedTrip === "") {
      return;
    }

    try {
      const response = await axiosInstance.get(`/bucket-list/${selectedTrip}`);
      if (response.data.success) {
        const tripMembers = response.data.data.allowed_users;
        const currUserInd = tripMembers.findIndex(
          (m) => m.email === localStorage.getItem("email")
        );
        tripMembers[currUserInd].username = "Personal";
        setTripMembers(tripMembers);
      } else {
        toast({
          title: "Error",
          description: "Failed to fetch trip members: Invalid data format.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      // toast({
      //   title: "Error",
      //   description: "Failed to fetch trip members.",
      //   status: "error",
      //   duration: 3000,
      //   isClosable: true,
      // });
    }
  };

  useEffect(() => {
    fetchPackingLists();
  }, [fetchPackingLists]);

  const fetchTrips = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get("/bucket-list/budget/packing_list");
      setTrips(response.data.data); // Set trips from the API response
    } catch (error) {
      console.error("Error fetching trips:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTripMembers();
    fetchTrips();
  }, [selectedTrip]);

  useEffect(() => {
    if (tripId && tripId !== "") {
      setSelectedTrip(tripId);
    }
    // Only run this effect when tripId changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]);

  // Handle deleting the packing list
  const handleDeletePackingList = async () => {
    try {
      const response = await axiosInstance.delete(
        `/packing_lists/${selectedListId}`
      );
      if (response.data.success) {
        toast({
          title: "Success",
          description: "Packing list deleted successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        setSelectedIdList(null);
        setPackingLists(
          packingLists.filter(
            (list) => list._id !== response.data.data.deleted_id
          )
        );
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete packing list.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle downloading the packing list
  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const pdfDoc = await PDFDocument.create();
      let page = pdfDoc.addPage([600, 800]);
      const { width, height } = page.getSize();
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      const greenColor = rgb(0.2196, 0.4627, 0.1137);
      const MARGIN = 50;
      const USABLE = width - MARGIN * 2;
      const RIGHT_X = MARGIN + USABLE / 2;
      const DIVIDER_Y = height - 70;

      const logoBytes = await fetch(officialLogo).then((res) =>
        res.arrayBuffer()
      );
      const logoImage = await pdfDoc.embedPng(logoBytes);
      const logoDims = { width: 140, height: 25 };

      const qrBytes = await fetch(qrCode).then((res) => res.arrayBuffer());
      const qrImage = await pdfDoc.embedPng(qrBytes);
      const qrDims = { width: 150, height: 150 };

      const startX = 50;
      const startY = height - 110 - 24;
      const columnWidth = (width - 100) / 2;

      const translatedTripName = await translateText(tripName);

      const QR_TOP_Y = 60 + qrDims.height; // y position of QR code + its height

      const wrap = (txt, fnt, fs, max) => {
        const words = String(txt).split(" ");
        const lines = [];
        let cur = "";
        for (const w of words) {
          const test = cur ? `${cur} ${w}` : w;
          if (fnt.widthOfTextAtSize(test, fs) <= max) cur = test;
          else {
            if (cur) lines.push(cur);
            cur = w;
          }
        }
        if (cur) lines.push(cur);
        return lines;
      };

      /* ───────── Header ───────── */
      const drawHeader = async () => {
        page.drawImage(logoImage, {
          x: 50,
          y: height - 50,
          width: logoDims.width,
          height: logoDims.height,
        });

        const titleText = await translateText("Packing ");
        page.drawText(titleText, {
          x: 50,
          y: height - 90,
          size: 24,
          font: boldFont,
          color: greenColor,
        });

        const prevWidth = font.widthOfTextAtSize(titleText, 24);
        const checklistText = await translateText("Checklist");
        page.drawText(checklistText, {
          x: 65 + prevWidth,
          y: height - 90,
          size: 24,
          font,
          color: greenColor,
        });

        wrap(translatedTripName, boldFont, 12, USABLE / 2 - 10).forEach(
          (ln, i) =>
            page.drawText(ln, {
              x: RIGHT_X,
              y: height - 35 - i * 14,
              size: 12,
              font: boldFont,
            })
        );

        const travelDatesText = await translateText("Travel Dates: -");
        page.drawText(travelDatesText, {
          x: 300,
          y: DIVIDER_Y,
          size: 12,
          font: boldFont,
        });

        page.drawRectangle({
          x: 50,
          y: height - 110,
          width: columnWidth * 2,
          height: 0.5,
          color: rgb(0.75, 0.75, 0.75),
        });
      };

      /* ───────── Footer ───────── */
      const drawFooter = async (isLastPage = false) => {
        if (isLastPage) {
          page.drawImage(qrImage, {
            x: width - qrDims.width - 50,
            y: 60,
            width: qrDims.width,
            height: qrDims.height,
          });
        }
        page.drawRectangle({
          x: 0,
          y: 0,
          width,
          height: 55,
          color: greenColor,
        });

        const msgText = await translateText("Stay Wild, Explore More");
        const msgTextWidth = font.widthOfTextAtSize(msgText, 16);
        page.drawText(msgText, {
          x: width / 2 - msgTextWidth / 2,
          y: 37,
          size: 16,
          font: boldFont,
          color: rgb(1, 1, 1),
        });

        const websiteText = "www.nomad360.io"; // domain literal
        const websiteTextWidth = font.widthOfTextAtSize(websiteText, 11);
        page.drawText(websiteText, {
          x: width / 2 - websiteTextWidth / 2,
          y: 20,
          size: 11,
          font: boldFont,
          color: rgb(1, 1, 1),
        });

        const copyright =
          "© 2025 NOMAD360. All Rights Reserved. Unauthorized distribution, duplication, or reproduction of this material is strictly prohibited.";
        const cpTextWidth = font.widthOfTextAtSize(copyright, 7);
        page.drawText(copyright, {
          x: width / 2 - cpTextWidth / 2,
          y: 5,
          size: 7,
          font: boldFont,
          color: rgb(1, 1, 1),
        });
      };

      /* manage columns & pagination */
      const moveColumnOrPage = async (newYOffset, isLastPage = false) => {
        let yLimit = 80;
        if (isLastPage && column === 1) {
          yLimit = QR_TOP_Y + 10; // add a little padding
        }
        if (y < yLimit && column === 0) {
          column = 1;
          x = startX + columnWidth;
          y = startY - newYOffset;
        } else if (y < yLimit && column === 1) {
          await drawFooter(isLastPage);
          page = pdfDoc.addPage([600, 800]);
          // update page context for new page
          const newPage = pdfDoc.getPages()[pdfDoc.getPageCount() - 1];
          page = newPage;
          await drawHeader();
          x = startX;
          y = startY;
          column = 0;
        }
      };

      /* ───────── Document Body ───────── */
      let y = startY;
      let x = startX;
      let column = 0;

      await drawHeader();

      for (const { packing_category, packing_list_items } of packingLists) {
        await moveColumnOrPage(0);

        const translatedCategory = await translateText(packing_category);
        page.drawRectangle({
          x: x,
          y: y - 15,
          width: columnWidth - 10,
          height: 20,
          color: greenColor,
        });
        const textWidth = font.widthOfTextAtSize(translatedCategory, 14);
        page.drawText(translatedCategory, {
          x: x + columnWidth / 2 - textWidth / 2 - 10,
          y: y - 10,
          size: 14,
          font: boldFont,
          color: rgb(1, 1, 1),
        });
        y -= 40;

        for (const { name } of packing_list_items) {
          await moveColumnOrPage(6);
          const translatedName = await translateText(name);
          page.drawRectangle({
            x: x,
            y: y,
            width: 10,
            height: 10,
            borderColor: rgb(0, 0, 0),
            borderWidth: 1,
          });
          page.drawText(translatedName, { x: x + 20, y: y, size: 12, font });
          y -= 20;
        }
        y -= 10;
      }

      await drawFooter(true);

      /* ───────── Save & Download ───────── */
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style = "display: none";
      a.href = url;
      a.download = `${translatedTripName}_Checklist.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast({
        title: "Download started!",
        description: "Your packing list PDF is downloading.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate or download PDF.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.post(
        `/packing_lists/${selectedTrip}/generate`
      );
      if (response.data.success) {
        toast({
          title: "Success",
          description: "Packing list created successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        setPackingLists([...packingLists, ...response.data.data]);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create packing list.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
    setLoading(false);
  };

  const redirectToChat = () => {
    history.push("/ai-travel-planner/chat");
  };

  const redirectToBucketList = () => {
    history.push("/ai-travel-planner/bucket-list");
  };

  /* ---------- NEW state + ref ---------- */
  const listRef = useRef(null);
  const [showScrollUp, setShowScrollUp] = useState(false);

  /* Show button only after scrolling ~250 px */
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const onScroll = () => setShowScrollUp(el.scrollTop > 250);
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () =>
    listRef.current?.scrollTo({ top: 0, behavior: "smooth" });

  if (isLoading) {
    return <Loading fullScreen />;
  }

  return (
    <>
      <Helmet>
        <title>AI Packing List&nbsp;| NOMAD360</title>
      </Helmet>

      <Flex
        h={{ base: "calc(100vh - 64px)", md: "100vh", lg: "100vh" }}
        overflowY="auto"
        p={0}
        bg="gray.50"
        direction={{ base: "column", md: "row", lg: "row" }}
      >
        {!isLoading && trips.length === 0 ? (
          <Center w="100%" bg="white">
            <Box textAlign="center" mb={4}>
              <Icon
                as={FaSuitcase}
                boxSize={12}
                color={colorMode === "dark" ? "#1A202C" : "#1A202C"}
              />
              <Text
                fontSize="2xl"
                fontWeight="bold"
                mt={2}
                color={colorMode === "dark" ? "#1A202C" : "#1A202C"}
                mb={0}
              >
                <Translate text="Your Packing List is Empty" />
              </Text>
              <Text
                fontSize="md"
                mt={2}
                color={colorMode === "dark" ? "#1A202C" : "#1A202C"}
                mb={0}
              >
                <Translate text="You don't have any trips planned at the moment." />
              </Text>
              <Text
                fontSize="md"
                mt={2}
                color={colorMode === "dark" ? "#1A202C" : "#1A202C"}
                mb={0}
              >
                <Translate text="Start planning your next adventure or enable your saved trip!" />
              </Text>
              <HStack alignContent="center" justifyContent="center">
                <Button
                  mt={6}
                  bg="#286328"
                  color="white"
                  _hover="none"
                  borderRadius="lg"
                  onClick={redirectToChat}
                >
                  <Translate text="Plan a trip" />
                </Button>
                <Divider
                  orientation="vertical"
                  h="40px"
                  borderColor={colorMode === "dark" ? "black" : "black"}
                  mt={6}
                  mb={0}
                />
                <Button
                  mt={6}
                  bg="#286328"
                  color="white"
                  _hover="none"
                  borderRadius="lg"
                  onClick={redirectToBucketList}
                >
                  <Translate text="Bucket list" />
                </Button>
              </HStack>
            </Box>
          </Center>
        ) : (
          <>
            <Box
              w={{ base: "100%", md: "70%", lg: "70%" }}
              h={{ base: "calc(100vh - 64px)", md: "100vh", lg: "100vh" }}
              overflowY="hidden"
              // p={5}
              // pt={4}
              bg="#FAFDFC"
              borderRadius="none"
              boxShadow="none"
            >
              <HStack
                p={2}
                pl={4}
                pr={4}
                justifyContent="space-between"
                alignItems="center"
                mb={4}
                borderBottom="1px"
                borderColor="blackAlpha.100"
                height="8vh"
              >
                <Text
                  fontSize="lg"
                  fontWeight="bold"
                  color="blackAlpha.800"
                  mb={0}
                >
                  <Translate text="Packing list" />
                </Text>

                {selectedTrip && (
                  <Box display="flex" alignItems="center">
                    {/* ───────── desktop / tablet buttons ───────── */}
                    <Box
                      display={{ base: "none", md: "flex" }}
                      alignItems="center"
                    >
                      <TravelGroupCircles
                        tripId={selectedTrip}
                        max="3"
                        availableResource="packing_list"
                      />
                      {packingLists.length > 0 && (
                        <Button
                          mr={2}
                          bg="#286328"
                          color="white"
                          _hover="none"
                          size="sm"
                          borderRadius="lg"
                          isLoading={loading}
                          onClick={handleGenerate}
                        >
                          <Translate text="Generate AI Packing List" />
                        </Button>
                      )}
                      <Button
                        bg="#286328"
                        color="white"
                        _hover="none"
                        size="sm"
                        borderRadius="lg"
                        onClick={openModal}
                        mr={1}
                      >
                        + 
                        <Translate text="Add category" />
                      </Button>
                      {/* download icon stays visible on all sizes */}
                      <Tooltip label="Download">
                        <IconButton
                          mr={2}
                          size="sm"
                          icon={
                            downloading ? (
                              <Spinner size="sm" color="#286328" />
                            ) : (
                              <Box as={DownloadIcon} boxSize="5" />
                            )
                          }
                          aria-label="Download"
                          bg="none"
                          _hover={{ bg: "none" }}
                          onClick={handleDownloadPDF}
                          isDisabled={downloading}
                        />
                      </Tooltip>
                      <Tooltip label="Invite User">
                        <IconButton
                          size="sm"
                          icon={<Box as={FaUserPlus} boxSize="5" />}
                          aria-label="Add User"
                          bg="none"
                          _hover={{ bg: "none" }}
                          onClick={inviteModal.onOpen}
                        />
                      </Tooltip>
                      <Tooltip label="Share">
                        <IconButton
                          size="sm"
                          icon={<FaShareAlt />}
                          onClick={onShareOpen}
                          aria-label="Share"
                          bg="none"
                          _hover="none"
                        />
                      </Tooltip>
                    </Box>

                    {/* ───────── "more" menu shown only on mobile ───────── */}
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        icon={<BsThreeDotsVertical />}
                        variant="ghost"
                        size="sm"
                        display={{ base: "inline-flex", md: "none" }}
                        aria-label="More options"
                      />
                      <MenuList>
                        {/* NEW — download option for mobile */}
                        <MenuItem
                          gap={2}
                          onClick={handleDownloadPDF}
                          isDisabled={downloading}
                        >
                          {downloading ? (
                            <Spinner size="xs" color="#286328" />
                          ) : (
                            <Box as={DownloadIcon} boxSize="5" />
                          )}
                          <Translate text="Download" />
                        </MenuItem>
                        <MenuItem gap={2} onClick={inviteModal.onOpen}>
                          <FaUserPlus />
                          <Translate text="Add User" />
                        </MenuItem>
                        <MenuItem gap={2}>
                          <FaShareAlt />
                          <Translate text="Share" />
                        </MenuItem>
                        <MenuItem onClick={handleGenerate}>
                          <FaClipboardList style={{ marginRight: "8px" }} />
                          <Translate text="Generate AI Packing List" />
                        </MenuItem>

                        <MenuItem onClick={openModal}>
                          <PlusSquareIcon mr={2} />
                          <Translate text="Add category" />
                        </MenuItem>
                      </MenuList>
                    </Menu>

                    {/* existing Settings menu */}
                    <SettingsMenu />
                  </Box>
                )}
              </HStack>

              <Box
                ref={listRef}
                p={5}
                pt={2}
                h={{ base: "calc(92vh - 64px)", md: "auto", lg: "auto" }}
                overflowY="auto"
              >
                <Heading fontSize="md" fontWeight="bold" mb={0}>
                  <Translate text="Trips" />
                </Heading>

                <Text fontSize="xs" color="gray.500" mb={2}>
                  <Translate text="Select Trip" />
                </Text>

                <PackingListTrips
                  selectedTrip={selectedTrip}
                  setSelectedTrip={setSelectedTrip}
                  setTripName={setTripName}
                  onTripSelect={setSelectedTrip}
                />
                {!selectedTrip && trips && (
                  <Flex alignItems="center" mt={7} mb={5}>
                    <TripsList
                      trips={trips}
                      setSelectedTrip={setSelectedTrip}
                    />
                  </Flex>
                )}
                {selectedTrip && (
                  <Heading fontSize="md" fontWeight="bold" mt={7} mb={0}>
                    <Translate text="Packing Overview" />
                  </Heading>
                )}
                {selectedTrip && (
                  <Flex alignItems="center" mt={2} mb={{ base: 2, md: 5 }}>
                    <Text fontWeight="bold" mr={3} mb={0} color="#286328">
                      {packingLists.flatMap((l) => l.packing_list_items)
                        .length > 0
                        ? (
                            (packingLists
                              .flatMap((l) => l.packing_list_items)
                              .filter((i) => i.completed).length /
                              packingLists.flatMap((l) => l.packing_list_items)
                                .length) *
                            100
                          ).toFixed(0)
                        : 0}
                      %
                    </Text>
                    <Progress
                      value={
                        packingLists.flatMap((l) => l.packing_list_items)
                          .length > 0
                          ? (packingLists
                              .flatMap((l) => l.packing_list_items)
                              .filter((i) => i.completed).length /
                              packingLists.flatMap((l) => l.packing_list_items)
                                .length) *
                            100
                          : 0
                      }
                      size="sm"
                      // colorScheme="#286328"
                      flex="1"
                      borderRadius="lg"
                      sx={{
                        "& > div": {
                          backgroundColor: "#286328",
                        },
                      }}
                    />
                  </Flex>
                )}

                {selectedTrip && isBaseScreen && (
                  <PackingListDetails
                    tripName={tripName}
                    packingLists={packingLists}
                    setPackingLists={setPackingLists}
                    selectedListId={selectedListId}
                    handleDeletePackingList={handleDeletePackingList}
                    isBaseScreen={isBaseScreen}
                  />
                )}
                <HStack justifyContent="space-between">
                  <VStack spacing={0} align="left">
                    {selectedTrip && (
                      <Heading
                        fontSize="md"
                        fontWeight="bold"
                        mt={{ base: 5, md: 4 }}
                        mb={0}
                      >
                        <Translate text="Packing" />
                      </Heading>
                    )}
                    {selectedTrip && (
                      <Text fontSize="xs" color="gray.500" mb={4}>
                        <Translate text="Select packing category" />
                      </Text>
                    )}
                    {packingLists.length === 0 && (
                    <Button
                      mr={2}
                      bg="#286328"
                      color="white"
                      _hover="none"
                      size="sm"
                      borderRadius="lg"
                      onClick={handleGenerate}
                      isLoading={loading}
                    >
                      <Translate text="Generate AI Packing List" />
                    </Button>
                  )}
                  </VStack>
                  
                </HStack>
                {selectedTrip && (
                  <Box
                    overflowY="auto"
                    sx={{
                      "&::-webkit-scrollbar": {
                        display: "none",
                      },
                      msOverflowStyle: "none",
                      scrollbarWidth: "none",
                    }}
                    h={{ base: "none", md: "46vh", lg: "46vh", xl: "60vh" }}
                  >
                    <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={4}>
                      {packingLists.map((l) => (
                        <PackingListCard
                          key={l._id}
                          packingList={l}
                          selectedListId={selectedListId}
                          setSelectedIdList={setSelectedIdList}
                          scrollToTop={scrollToTop}
                        />
                      ))}
                    </SimpleGrid>
                  </Box>
                )}
              </Box>
            </Box>

            {selectedTrip && !isBaseScreen && (
              <PackingListDetails
                tripName={tripName}
                packingLists={packingLists}
                setPackingLists={setPackingLists}
                selectedListId={selectedListId}
                handleDeletePackingList={handleDeletePackingList}
                isBaseScreen={isBaseScreen}
              />
            )}

            <PackingListModal
              isModalOpen={isModalOpen}
              closeModal={closeModal}
              selectedTrip={selectedTrip}
              tripMembers={tripMembers}
              fetchPackingLists={fetchPackingLists}
            />

            {/* Share Modal */}
            <Modal
              isOpen={isShareOpen}
              onClose={onShareClose}
              size={{ base: "xs", md: "md" }}
            >
              <ModalOverlay />
              <ModalContent>
                <ModalHeader>
                  <Translate text="Share Packing List" />
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  <Text mb={4}>
                    <Translate text="Anyone with this link can request access." />
                  </Text>
                  <Box
                    bg={colorMode === "light" ? "gray.100" : "gray.700"}
                    p={3}
                    borderRadius="md"
                    wordBreak="break-all"
                  >
                    {shareUrl}
                  </Box>
                </ModalBody>
                <ModalFooter>
                  <Button
                    onClick={onCopy}
                    bg="black"
                    color="white"
                    _hover="none"
                    _active="none"
                    mr={3}
                  >
                    <Translate text={hasCopied ? "Copied" : "Copy Link"} />
                  </Button>
                  <Button variant="ghost" onClick={onShareClose}>
                    <Translate text="Close" />
                  </Button>
                </ModalFooter>
              </ModalContent>
            </Modal>
            <InviteManagerModal
              tripId={selectedTrip}
              isOpen={inviteModal.isOpen}
              onClose={inviteModal.onClose}
              availableResource="packing_list"
            />
          </>
        )}
        {/* ② Scroll‑up button */}
        {showScrollUp && (
          <IconButton
            icon={<ArrowUpIcon boxSize={5} />}
            position="fixed"
            bottom={{ base: 16, md: 6 }}
            right={{ base: 4, md: 8 }}
            size="md"
            bg="#1A202C"
            color="white"
            _hover={{ bg: "#1A202C" }}
            borderRadius="full"
            zIndex="1200"
            onClick={scrollToTop}
          />
        )}
      </Flex>
    </>
  );
};

export default PackingListMenu;
